/**
 * Classic mission list (04 §1): any of the twelve missions in its native terrain, at one of the
 * slider's four difficulties. Picking one goes to the briefing; the loadout after it packs free.
 */

import '../rosters.css';
import type { ArchetypeId } from '../../../sim/types';
import { archetypeNumber, CLASSIC_ARCHETYPES, classicParams, freshSeed } from '../archetypes';
import type { Ctx, Screen } from '../context';
import { box, button, el } from '../dom';
import { DIFFICULTY_NAMES, serviceName } from '../text';
import { choiceRow } from '../widgets/choiceRow';
import { frame } from '../widgets/frame';

/** Standard: the slider's second detent. */
const DEFAULT_DIFFICULTY = 1;

export function classicMissionScreen(ctx: Ctx, rangerId: string): Screen {
  const profile = ctx.api.profile();
  const ranger = profile.classicRoster.find((r) => r.id === rangerId);
  const f = frame('classic-missions', 'CLASSIC - SELECT MISSION');
  f.info.append(el('span', '', ranger === undefined ? 'NOBODY ASSIGNED' : `ASSIGNED: ${serviceName(ranger)}`));

  let difficulty = DEFAULT_DIFFICULTY;
  const threatCells: Array<[id: ArchetypeId, cell: HTMLElement]> = [];
  const paintThreat = (): void => {
    for (const [id, cell] of threatCells) cell.textContent = `TR ${classicParams(id, difficulty, 1).threat}`;
  };

  const about = el('div', 'menu-prose menu-paper');
  const aboutTitle = el('div', 'menu-panel-title', 'MISSION');
  const describeMission = (id: ArchetypeId): void => {
    const d = ctx.describe(id);
    aboutTitle.textContent = d.name;
    about.textContent = d.text;
  };

  const list = el('div', 'menu-mission-list menu-scroll menu-grow');
  let first: HTMLButtonElement | null = null;
  for (const info of CLASSIC_ARCHETYPES) {
    const go = (): void =>
      ctx.nav.briefing({
        mode: 'classic',
        params: classicParams(info.id, difficulty, freshSeed()),
        node: null,
        rangerId,
      });
    const row = button('', go, 'menu-mission-row');
    const threat = el('span', 'menu-mission-threat');
    const best = profile.bestScores[info.id];
    row.append(
      el('span', 'menu-mission-number', archetypeNumber(info.id)),
      el('span', 'menu-mission-name', ctx.describe(info.id).name),
      el('span', 'menu-mission-terrain', ctx.describe(info.biome).name),
      threat,
      el('span', 'menu-mission-best', best === undefined ? '' : `BEST ${Math.round(best).toLocaleString('en-US')}`),
    );
    row.addEventListener('focus', () => describeMission(info.id));
    row.addEventListener('mouseenter', () => describeMission(info.id));
    // Nobody to send: the list can be read, but nothing on it can be flown.
    row.disabled = ranger === undefined;
    threatCells.push([info.id, threat]);
    list.append(row);
    first ??= row;
  }
  paintThreat();

  const slider = choiceRow(
    'DIFFICULTY',
    DIFFICULTY_NAMES.map((label, value) => ({ value, label })),
    difficulty,
    (value) => {
      difficulty = value;
      paintThreat();
    },
  );
  const back = button('BACK', () => ctx.nav.classic());
  const side = box(
    'menu-col menu-side',
    box('menu-panel menu-col menu-grow', aboutTitle, box('menu-scroll menu-grow', about)),
    el('div', 'menu-prose menu-dim menu-small', 'NATIVE TERRAIN, CLEAR DAY. THREAT = THE MISSION\'S OWN + THE SLIDER.'),
    back,
  );
  const firstInfo = CLASSIC_ARCHETYPES[0];
  if (firstInfo !== undefined) describeMission(firstInfo.id);

  f.body.append(box('menu-col menu-grow', slider.el, list), side);
  return {
    el: f.el,
    back: () => ctx.nav.classic(),
    initialFocus: () => (ranger === undefined ? back : first),
  };
}
